import { useEffect } from 'react';
import { siteInfo } from '../data/siteInfo';

function buildAbsoluteUrl(path = '/') {
  return new URL(path, siteInfo.siteUrl).toString();
}

function upsertMeta(attribute, key, content) {
  if (!content) {
    return;
  }

  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) {
    return;
  }

  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function buildPageSchema({ title, description, canonicalUrl }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: canonicalUrl,
    inLanguage: 'de-CH',
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.siteUrl
    }
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: buildAbsoluteUrl(item.path)
    }))
  };
}

export function buildCafeSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'CafeOrCoffeeShop',
    name: siteInfo.name,
    description: siteInfo.defaultDescription,
    url: siteInfo.siteUrl,
    image: buildAbsoluteUrl(siteInfo.ogImagePath),
    telephone: siteInfo.phoneIntl,
    email: siteInfo.email,
    priceRange: '$$',
    servesCuisine: ['Cafe', 'Brunch', 'Dessert', 'Lunch'],
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.streetAddress,
      postalCode: siteInfo.postalCode,
      addressLocality: siteInfo.addressLocality,
      addressCountry: siteInfo.addressCountry
    },
    sameAs: [siteInfo.instagramUrl],
    openingHoursSpecification: siteInfo.openingHours.flatMap((entry) =>
      entry.schemaDays.map((day) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: `https://schema.org/${day}`,
        opens: entry.opens,
        closes: entry.closes
      }))
    )
  };
}

export function Seo({
  title,
  description = siteInfo.defaultDescription,
  path = '/',
  type = 'website',
  schema = []
}) {
  useEffect(() => {
    const pageTitle = title ? `${title} | ${siteInfo.name}` : siteInfo.defaultTitle;
    const canonicalUrl = buildAbsoluteUrl(path);
    const ogImageUrl = buildAbsoluteUrl(siteInfo.ogImagePath);
    const schemaItems = [
      buildPageSchema({ title: pageTitle, description, canonicalUrl }),
      ...(Array.isArray(schema) ? schema : [schema]).filter(Boolean)
    ];

    document.documentElement.lang = 'de';
    document.title = pageTitle;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1');
    upsertMeta('property', 'og:locale', siteInfo.locale);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', pageTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:site_name', siteInfo.name);
    upsertMeta('property', 'og:image', ogImageUrl);
    upsertMeta('name', 'twitter:card', 'summary');
    upsertMeta('name', 'twitter:title', pageTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImageUrl);
    upsertLink('canonical', canonicalUrl);

    document.head
      .querySelectorAll('script[data-seo-jsonld="true"]')
      .forEach((element) => element.remove());

    schemaItems.forEach((item) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.text = JSON.stringify(item);
      document.head.appendChild(script);
    });
  }, [description, path, schema, title, type]);

  return null;
}
